import { Organization } from '@lib/types/api/Organization';

/**
 * Single implementation of active-org resolution (CPS-7688):
 * cookie -> account default organization -> first organization.
 *
 * Failure policy: when the organization list is unavailable (fetch failed) or
 * empty, this returns undefined and callers must treat it as "no org context".
 * Read-only surfaces may degrade (fail open); surfaces that write on behalf of
 * the resolved org (e.g. the claim route) must fail closed instead of
 * transacting. The resolved org always comes from `organizations`, so it is by
 * construction one the user is a member of — never trust the raw cookie value.
 */
export const resolveActiveOrg = (
  cookieValue: string | undefined,
  organizations: Organization[] | undefined,
  defaultOrganizationId?: string
): Organization | undefined => {
  if (!organizations?.length) {
    return undefined;
  }
  return (
    organizations.find((org) => org.id === cookieValue) ??
    organizations.find((org) => org.id === defaultOrganizationId) ??
    organizations[0]
  );
};

/**
 * Server-side variant that fetches the account default lazily, so a valid
 * cookie costs no extra call while a stale cookie (naming an org the user has
 * left — the cookie lives 400 days, CPS-7538) still lands on the account
 * default instead of an arbitrary first org.
 */
export const resolveActiveOrgWithDefaultFetch = async (
  cookieValue: string | undefined,
  organizations: Organization[] | undefined,
  getDefaultOrganizationId: () => Promise<string | undefined>
): Promise<Organization | undefined> => {
  if (!organizations?.length) {
    return undefined;
  }
  const cookieOrg = organizations.find((org) => org.id === cookieValue);
  if (cookieOrg) {
    return cookieOrg;
  }
  return resolveActiveOrg(
    undefined,
    organizations,
    await getDefaultOrganizationId()
  );
};
